import { createFileRoute, useParams, useSearch, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../../../contexts/LocaleContext'
import { useAssets, useAssetTypes, useAssetCount } from '../../../api/queries'
import { useAssetWithParentChain } from '../../../hooks/useQueries'
import { useLiveSubscription } from '../../../contexts/LiveEventsContext'
import { useMoveAsset } from '../../../hooks/useQueries'
import { useCan } from '../../../contexts/PermissionsContext'
import { mdiFileTree, mdiPlus, mdiMagnify, mdiAlert, mdiDomain, mdiPencil, mdiArchiveOutline, mdiDatabaseArrowDownOutline } from '@mdi/js'
import { AppIcon } from '../../../components/ui/icon'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { TreeView } from '../../../components/TreeView'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../../../components/ApiSearchableSelect'
import { assetTypeFilterOptions } from '../../../lib/asset-type-options'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { EmptyState } from '../../../components/ui/empty-state'
import { Card } from '../../../components/ui/card'

export const Route = createFileRoute('/$org/assets/')({
    component: RouteComponent,
    // `import_uid` stays OPTIONAL in the returned type on purpose — see the
    // same note on the documents route.
    validateSearch: (search: Record<string, unknown>): {
        q: string | undefined
        flag: string | undefined
        asset_type_code: string | undefined
        asset_type_uid: string | undefined
        showBuildingGroups: string | undefined
        showBuildings: string | undefined
        archived: string | undefined
        import_uid?: string
        ancestor_uid?: string
    } => {
        return {
            q: search.q ? String(search.q) : undefined,
            flag: search.flag ? String(search.flag) : undefined,
            asset_type_code: search.asset_type_code ? String(search.asset_type_code) : undefined,
            // Legacy: kept so old bookmarks with asset_type_uid still work; resolved to code on first navigate.
            asset_type_uid: search.asset_type_uid ? String(search.asset_type_uid) : undefined,
            showBuildingGroups: search.showBuildingGroups ? String(search.showBuildingGroups) : undefined,
            showBuildings: search.showBuildings ? String(search.showBuildings) : undefined,
            archived: search.archived === 'true' ? 'true' : undefined,
            // Import drill-down (spec 2026-07-27-10 §7): the dashboard's asset
            // cards navigate here. Present ⇒ the tree renders the flat result
            // set of that import instead of the building-group roots.
            import_uid: search.import_uid ? String(search.import_uid) : undefined,
            // Network drill-down (spec 2026-08-16-03 §5): present ⇒ the tree
            // shows the whole subtree of that asset, aggregated members
            // included (the rollup writes parent_asset_ids).
            ancestor_uid: search.ancestor_uid ? String(search.ancestor_uid) : undefined,
        }
    },
})

function RouteComponent() {
    const { t } = useTranslation('assets')
    const { locale } = useLocale()
    const { org } = useParams({ from: '/$org/assets/' })
    const { q, flag, showBuildingGroups, showBuildings, asset_type_code, asset_type_uid, archived, import_uid, ancestor_uid } = useSearch({ from: '/$org/assets/' })
    const navigate = useNavigate({ from: '/$org/assets' as any })

    // Live entity notifications: refetch the list within ~1-2s of any asset
    // change anywhere in the org, instead of waiting out the polling loop.
    useLiveSubscription({ entity: 'assets' })

    // Get asset_uid from URL search params manually
    const urlSearchParams = new URLSearchParams(window.location.search)
    const asset_uid = urlSearchParams.get('asset_uid') || undefined

    // Local state for search - completely independent of URL to prevent re-renders
    const [searchValue, setSearchValue] = useState('')
    const [activeSearchTerm, setActiveSearchTerm] = useState('') // This drives the API call
    const searchInputRef = useRef<HTMLInputElement>(null)
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isInitialized = useRef(false)

    // Edition mode state for drag-and-drop functionality
    const [editionMode, setEditionMode] = useState(false)
    // The move's outcome, shown inline under the tree — never a toast.
    const [moveError, setMoveError] = useState<string | null>(null)
    const moveAsset = useMoveAsset(org)
    // Restructuring is a write: no edit mode without `assets.write`.
    const canWriteAssets = useCan('assets.write')

    // Query client for cache invalidation

    // API hook for asset type search

    // Initialize from URL only once on mount to prevent focus loss
    useEffect(() => {
        if (!isInitialized.current && q) {
            setSearchValue(q)
            setActiveSearchTerm(q)
            isInitialized.current = true
        }
    }, [q])

    // Debounced URL update
    const updateSearchURL = useCallback((searchTerm: string) => {
        navigate({
            search: {
                q: searchTerm || undefined,
                flag,
                showBuildingGroups,
                showBuildings,
                asset_type_code,
                asset_type_uid,
            } as any
        })
        // Manually update asset_uid in URL if needed
        if (asset_uid) {
            const url = new URL(window.location.href)
            url.searchParams.set('asset_uid', asset_uid)
            window.history.replaceState({}, '', url.toString())
        }
    }, [navigate, asset_uid, flag, showBuildingGroups, showBuildings, asset_type_code, asset_type_uid])

    // Set up breadcrumbs
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('common:nav.assets'), path: `/${org}/assets` }
    ])

    // Load asset types
    const {
        data: assetTypes = [],
        isLoading: assetTypesLoading,
        error: assetTypesError
    } = useAssetTypes(org)

    // The type filter's options come from the same query as the page: it
    // retries, refetches, and its error shows on the page, where a second,
    // one-shot fetch left the filter silently empty on a 403 (#1720).
    const initialAssetTypeOptions = useMemo(
        () => assetTypeFilterOptions(assetTypes, locale),
        [assetTypes, locale],
    )

    const searchAssetTypes = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        // For asset types, we do client-side filtering since we load all types upfront
        if (query && query.length >= 2) {
            const lowerQuery = query.toLowerCase()
            const filtered = initialAssetTypeOptions.filter((option: ApiSearchableSelectOption) =>
                option.label.toLowerCase().includes(lowerQuery)
            )
            return filtered
        }

        return initialAssetTypeOptions
    }, [initialAssetTypeOptions])

    // Get UIDs for building_group and building types
    const buildingGroupTypeUid = assetTypes.find((t: any) => t.code === 'building_group')?.uid
    const buildingTypeUid = assetTypes.find((t: any) => t.code === 'building')?.uid

    // Resolve effective asset-type filter — `asset_type_code` is the canonical, portable URL form.
    // `asset_type_uid` is honoured for legacy bookmarks but normalised to a code on subsequent navigates.
    const effectiveAssetTypeCode = useMemo(() => {
        if (asset_type_code) return asset_type_code
        if (asset_type_uid) {
            return assetTypes.find((t: any) => t.uid === asset_type_uid)?.code as string | undefined
        }
        return undefined
    }, [asset_type_code, asset_type_uid, assetTypes])

    const effectiveAssetTypeUid = useMemo(() => {
        if (effectiveAssetTypeCode) {
            return assetTypes.find((t: any) => t.code === effectiveAssetTypeCode)?.uid as string | undefined
        }
        return undefined
    }, [effectiveAssetTypeCode, assetTypes])

    // Fetch incomplete asset counts
    const { data: incompleteBuildingGroupCount = 0 } = useAssetCount(org, buildingGroupTypeUid, 'incomplete')
    const { data: incompleteBuildingCount = 0 } = useAssetCount(org, buildingTypeUid, 'incomplete')

    // Determine which asset types to filter by
    const assetTypeFilter = useMemo(() => {
        if (effectiveAssetTypeUid) {
            return [effectiveAssetTypeUid]
        }
        // An import or network drill-down is a flat result set across every
        // type — never narrowed to building-group roots.
        if (import_uid || ancestor_uid) {
            return undefined
        }
        // When not searching and not filtering by asset type, show only building groups as root
        if (!activeSearchTerm) {
            return buildingGroupTypeUid ? [buildingGroupTypeUid] : []
        }
        return undefined // Search all types when searching
    }, [effectiveAssetTypeUid, activeSearchTerm, buildingGroupTypeUid, import_uid, ancestor_uid])

    // Load root assets (building groups) for the TreeView when NOT searching
    const {
        data: rootAssets = [],
        isLoading: rootAssetsLoading,
        error: rootAssetsError,
    } = useAssets(
        org,
        assetTypeFilter, // Use asset type filter
        undefined, // No search query for root assets
        undefined, // No parent filter for root assets
        'type',
        ['children_count', 'flags', 'external_ids', 'properties', 'link_counts'],
        flag, // Add flag filter
        false, // No asset chain for root assets
        archived === 'true'
    )

    // Load search results with parent chain when searching - uses local state, not URL
    const {
        data: searchResults = [],
        isLoading: searchLoading,
        error: searchError,
    } = useAssets(
        org,
        activeSearchTerm ? (effectiveAssetTypeUid ? [effectiveAssetTypeUid] : undefined) : assetTypeFilter, // Use asset type filter for both search and non-search
        activeSearchTerm || undefined, // Search query from local state
        undefined, // No parent filter for search
        'name',
        ['children_count', 'flags', 'external_ids', 'properties', 'link_counts'],
        flag, // Add flag filter
        !!activeSearchTerm, // Enable asset chain when searching
        archived === 'true',
        undefined, // inTree: unfiltered
        import_uid, // Import drill-down (spec 2026-07-27-10 §7)
        ancestor_uid // Network drill-down (spec 2026-08-16-03 §5)
    )

    // Build tree structure from flat search results
    const buildTreeFromFlatList = useCallback((assets: any[]) => {
        if (!assets.length) return []

        // Create a map for quick lookup
        const assetMap = new Map()
        assets.forEach(asset => assetMap.set(asset.uid, { ...asset, children: [] }))

        // Build tree structure
        const roots: any[] = []
        assets.forEach(asset => {
            const assetWithChildren = assetMap.get(asset.uid)
            if (asset.parent_asset_uid && assetMap.has(asset.parent_asset_uid)) {
                // Add as child to parent
                const parent = assetMap.get(asset.parent_asset_uid)
                parent.children.push(assetWithChildren)
            } else {
                // This is a root node
                roots.push(assetWithChildren)
            }
        })

        return roots
    }, [])

    // Use search results when searching or drilling into an import (build a
    // tree out of the flat list), otherwise use the root assets.
    const flatResultMode = !!activeSearchTerm || !!import_uid || !!ancestor_uid
    const assetsToShow = flatResultMode ? buildTreeFromFlatList(searchResults) : rootAssets
    const isTreeLoading = flatResultMode ? searchLoading : rootAssetsLoading
    const treeError = flatResultMode ? searchError : rootAssetsError

    // Auto-expand the parent chain when an `?asset_uid=` is present on the URL.
    // The S1 rework removed the right-side preview pane, but we still honour
    // the legacy query param for deep-linking.
    const { data: parentChainAssets = [] } = useAssetWithParentChain(org, asset_uid || '')
    const parentChain = parentChainAssets.map((asset: any) => asset.uid)

    // Handle asset move via drag and drop
    // Moving an asset by dragging it in the tree (spec 2026-09-18-01).
    //
    // Two things were wrong here and both were silent. The body sent
    // `parent_asset_uid: undefined` for a drop on the root, i.e. it left the
    // field OUT — so detaching an asset to the root did nothing at all; the
    // shared `parentAssetPatchBody` sends the explicit `null` the handler reads
    // as a detach. And every refusal (a cycle, an archived end, a type the
    // ontology does not accept under that parent) went to `console.error`
    // behind a TODO, leaving the user with a tree that simply snapped back.
    const handleAssetMove = useCallback(async (assetUid: string, newParentUid: string | null) => {
        setMoveError(null)
        try {
            await moveAsset.moveAsync(assetUid, newParentUid)
        } catch (error) {
            // openapi-fetch throws the parsed error BODY, whose `title` is the
            // sentence the API wrote for a human.
            const body = error as { title?: string } | undefined
            setMoveError(body?.title || (error instanceof Error ? error.message : String(error)))
        }
    }, [moveAsset])

    // Handle search input changes - single 100ms debounce for both search and URL
    const handleSearchChange = useCallback((value: string) => {
        // Update input value immediately for responsive UI
        setSearchValue(value)

        // Clear previous timeout
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current)
        }

        // Single 100ms debounce for both search API and URL update
        searchDebounceRef.current = setTimeout(() => {
            setActiveSearchTerm(value)
            updateSearchURL(value)
        }, 100)
    }, [updateSearchURL])

    const handleClearSearch = useCallback(() => {
        setSearchValue('')
        setActiveSearchTerm('')
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current)
        }
        updateSearchURL('')
    }, [updateSearchURL])

    const loading = assetTypesLoading || isTreeLoading
    const error = assetTypesError || treeError

    return (
        <div className="space-y-4 md:space-y-6">
            <PageHeader
                icon={SECTION_ICONS.assets}
                title={t('list.title')}
                description={t('list.subtitle')}
                actions={
                <>
                    {/* Single Import/Export link — icon-only on mobile, labelled on desktop. */}
                    <Link to="/$org/data" params={{ org }}>
                        <Button
                            variant="outline"
                            data-testid="assets-actions-menu"
                            aria-label={t('list.import_export', { defaultValue: 'Import / Export' })}
                        >
                            <AppIcon path={mdiDatabaseArrowDownOutline} size="md" className="md:mr-1" />
                            <span className="hidden md:inline">
                                {t('list.import_export', { defaultValue: 'Import / Export' })}
                            </span>
                        </Button>
                    </Link>

                    {/* Create — labelled on desktop, "+" only on mobile. */}
                    <Link
                        to="/$org/createBuildingGroup"
                        params={{ org }}
                        search={{}}
                    >
                        <Button
                            className="bg-primary hover:bg-primary/90"
                            aria-label={t('list.create')}
                        >
                            <AppIcon path={mdiPlus} size="md" className="md:mr-1" />
                            <span className="hidden md:inline">{t('list.create')}</span>
                        </Button>
                    </Link>
                </>
                }
            />

            {/* Incomplete Assets Summary */}
            {!flag && (incompleteBuildingGroupCount > 0 || incompleteBuildingCount > 0) && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-2">{t('list.incomplete_assets_title')}</h3>
                    <div className="flex gap-4 text-sm text-orange-700 dark:text-orange-300">
                        {incompleteBuildingGroupCount > 0 && (
                            <button
                                data-testid="incomplete-building-groups-btn"
                                onClick={() => navigate({
                                    search: {
                                        q,
                                        flag: 'incomplete',
                                        showBuildingGroups,
                                        showBuildings,
                                        asset_type_code: 'building_group',
                                        asset_type_uid: undefined,
                                    } as any
                                })}
                                className="flex items-center gap-2 hover:text-orange-900 dark:hover:text-orange-100 transition-colors cursor-pointer"
                            >
                                <AppIcon path={mdiDomain} size="sm" />
                                <span>{t('list.incomplete_building_groups', { count: incompleteBuildingGroupCount })}</span>
                            </button>
                        )}
                        {incompleteBuildingCount > 0 && (
                            <button
                                data-testid="incomplete-buildings-btn"
                                onClick={() => navigate({
                                    search: {
                                        q,
                                        flag: 'incomplete',
                                        showBuildingGroups,
                                        showBuildings,
                                        asset_type_code: 'building',
                                        asset_type_uid: undefined,
                                    } as any
                                })}
                                className="flex items-center gap-2 hover:text-orange-900 dark:hover:text-orange-100 transition-colors cursor-pointer"
                            >
                                <AppIcon path={SECTION_ICONS.assets} size="sm" />
                                <span>{t('list.incomplete_buildings', { count: incompleteBuildingCount })}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Search Field - Top - Always visible */}
            <Card className="p-4 space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex items-center gap-3 flex-1">
                        <AppIcon tone="muted" path={mdiMagnify} size="md" />
                        <Input
                            ref={searchInputRef}
                            type="text"
                            placeholder={asset_uid ? t('list.search_child_placeholder') : t('list.search_placeholder')}
                            value={searchValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="flex-1"
                        />
                        {searchValue && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearSearch}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                {t('list.clear_search')}
                            </Button>
                        )}
                    </div>

                    {/* Asset Type Filter */}
                    <ApiSearchableSelect
                        value={effectiveAssetTypeCode || ''}
                        placeholder={t('list.asset_type_placeholder')}
                        searchPlaceholder={t('create.asset_type_search_placeholder')}
                        emptyMessage={t('create.asset_type_empty')}
                        onSearch={searchAssetTypes}
                        initialOptions={initialAssetTypeOptions}
                        onValueChange={(value) => navigate({
                            search: {
                                q,
                                flag,
                                showBuildingGroups,
                                showBuildings,
                                asset_type_code: value || undefined,
                                asset_type_uid: undefined,
                            } as any
                        })}
                        className="w-full sm:w-48"
                        minSearchLength={0}
                        debounceMs={150}
                    />
                </div>

                {asset_uid && (
                    <div className="text-xs text-muted-foreground">
                        {activeSearchTerm ? t('list.filter_children_searching') : t('list.filter_children')}
                    </div>
                )}
                {/* Show when incomplete flag is active */}
                {flag === 'incomplete' && (
                    <div className="flex items-center text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 px-3 py-1 rounded-md border border-orange-200 dark:border-orange-800">
                        <AppIcon path={mdiAlert} size="sm" className="mr-2" />
                        {t('list.filter_incomplete')}
                        <button
                            onClick={() => navigate({
                                search: {
                                    q,
                                    flag: undefined,
                                    showBuildingGroups,
                                    showBuildings,
                                    asset_type_code,
                                    asset_type_uid,
                                } as any
                            })}
                            className="ml-2 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-200 text-xs underline"
                            title="Clear incomplete filter"
                        >
                            {t('list.clear_search')}
                        </button>
                    </div>
                )}
                {/* Network drill-down chip (spec 2026-08-16-03 §5): the tree is
                    scoped to one asset's whole subtree, aggregated members
                    included. Clearing it goes back to the roots. */}
                {ancestor_uid && (
                    <div
                        className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1 text-sm text-muted-foreground"
                        data-testid="assets-ancestor-filter"
                    >
                        <AppIcon path={mdiFileTree} size="sm" />
                        <span>{t('list.filter_ancestor', { defaultValue: 'Showing everything below one asset, aggregated members included.' })}</span>
                        <button
                            onClick={() => navigate({
                                search: {
                                    q,
                                    flag,
                                    showBuildingGroups,
                                    showBuildings,
                                    asset_type_code,
                                    asset_type_uid,
                                    ancestor_uid: undefined,
                                } as any
                            })}
                            className="text-xs underline"
                            data-testid="assets-ancestor-filter-clear"
                        >
                            {t('list.clear_search')}
                        </button>
                    </div>
                )}

                {/* Archived toggle */}
                <Button
                    variant={archived === 'true' ? 'default' : 'outline'}
                    size="sm"
                    data-testid="toggle-archived-btn"
                    onClick={() => navigate({
                        search: {
                            q,
                            flag,
                            showBuildingGroups,
                            showBuildings,
                            asset_type_code,
                            asset_type_uid,
                            archived: archived === 'true' ? undefined : 'true',
                        } as any
                    })}
                >
                    <AppIcon path={mdiArchiveOutline} size="sm" className="mr-1" />
                    {t('archive.filter_archived')}
                </Button>
            </Card>

            {/* Handle error state */}
            {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                    <div className="text-destructive text-sm">
                        {(error as any)?.title || (error as any)?.message || t('common:status.error')}
                    </div>
                </div>
            )}

            {/* Handle loading state */}
            {loading && (
                <div className="text-center text-muted-foreground py-8">
                    {t('list.loading')}
                </div>
            )}

            {/* Spec S1: full-width tree, the right-side preview panel is gone.
                Clicking a row navigates to /assets/{id}; the chevron expands. */}
            {/* Issue #1626: the empty list is the page-level panel, like
                Signatures, not a small glyph inside the tree card. */}
            {!loading && !error && assetsToShow.length === 0 && (
                <EmptyState
                    live={!!activeSearchTerm}
                    icon={SECTION_ICONS.assets}
                    title={
                        activeSearchTerm
                            ? t('list.empty_search', { query: activeSearchTerm })
                            : asset_uid
                                ? t('list.empty_children')
                                : t('list.empty_tree')
                    }
                    description={activeSearchTerm || asset_uid ? undefined : t('list.empty_tree_description')}
                    data-testid="assets-tree-empty"
                />
            )}
            {!loading && !error && assetsToShow.length > 0 && (
                <Card className="overflow-hidden p-4 flex flex-col">
                    {moveError && (
                        <p className="pb-2 text-sm text-destructive" data-testid="asset-move-error">{moveError}</p>
                    )}
                    <div className="overflow-y-auto pr-2 flex-1" style={{ maxHeight: 'calc(100vh - 360px)' }}>
                        <TreeView
                            orgCode={org}
                            rootAssets={assetsToShow}
                            assetTypes={assetTypes}
                            selectedAssetUid={asset_uid}
                            expandedNodeIds={parentChain}
                            isSearchResult={!!activeSearchTerm}
                            editionMode={editionMode && canWriteAssets}
                            onAssetMove={handleAssetMove}
                        />
                    </div>
                    <div className="mt-4 pt-4 border-t border-border flex justify-end">
                        {canWriteAssets && <Button
                            variant={editionMode ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setEditionMode(!editionMode)}
                            className="flex items-center gap-2"
                            data-testid="edition-mode-toggle"
                        >
                            <AppIcon path={mdiPencil} size="sm" />
                            {editionMode ? t('list.exit_edit') : t('list.edit_mode')}
                        </Button>}
                    </div>
                </Card>
            )}

        </div>
    )
}